import { FloatingChatConfig, InternalConfig } from './types';

// 默认配置
export const DEFAULT_CONFIG: InternalConfig = {
  baseUrl: typeof window !== 'undefined' ? window.location.origin : '',
  theme: 'light',
  primaryColor: '#1890ff',
  position: 'bottom-right',
  buttonSize: 50,
  windowWidth: 400,
  windowHeight: 600,
  fullScreen: false,
  showOnLoad: false,
  enableDrag: false,
  enableResize: false,
  enableSound: false,
  enableFullScreenToggle: true,
  saveSession: true,
  storageType: 'sessionStorage',
  title: 'AI智能助手',
  showBranding: true,
  showButton: true,
  onOpen: () => {},
  onClose: () => {},
  onMessage: () => {},
  onReady: () => {},
  onError: () => {},
};

// 配置管理器
export class ConfigManager {
  private config: InternalConfig;

  constructor(userConfig: FloatingChatConfig = {}) {
    this.config = this.mergeConfig(userConfig);
    this.validateConfig();
  }

  private mergeConfig(userConfig: FloatingChatConfig): InternalConfig {
    return {
      ...DEFAULT_CONFIG,
      ...userConfig,
    } as InternalConfig;
  }

  private validateConfig(): void {
    // 验证baseUrl
    if (!this.config.baseUrl) {
      throw new Error('FloatingChat: baseUrl is required');
    }

    // 验证颜色格式
    if (this.config.primaryColor && !this.isValidColor(this.config.primaryColor)) {
      console.warn('FloatingChat: Invalid primaryColor, using default');
      this.config.primaryColor = DEFAULT_CONFIG.primaryColor;
    }

    // 验证尺寸
    if (this.config.buttonSize < 40 || this.config.buttonSize > 100) {
      console.warn('FloatingChat: buttonSize should be between 40-100, using default');
      this.config.buttonSize = DEFAULT_CONFIG.buttonSize;
    }

    if (typeof this.config.windowWidth === 'number') {
      if (this.config.windowWidth < 300 || this.config.windowWidth > 800) {
        console.warn('FloatingChat: windowWidth should be between 300-800, using default');
        this.config.windowWidth = DEFAULT_CONFIG.windowWidth;
      }
    } else if (typeof this.config.windowWidth === 'string') {
      if (!/^\d+(px|vw|vh|%)$/.test(this.config.windowWidth)) {
        console.warn('FloatingChat: windowWidth string should end with px, vw, vh or %, using default');
        this.config.windowWidth = DEFAULT_CONFIG.windowWidth;
      }
    }

    if (typeof this.config.windowHeight === 'number') {
      if (this.config.windowHeight < 400 || this.config.windowHeight > 1000) {
        console.warn('FloatingChat: windowHeight should be between 400-1000, using default');
        this.config.windowHeight = DEFAULT_CONFIG.windowHeight;
      }
    } else if (typeof this.config.windowHeight === 'string') {
      if (!/^\d+(px|vw|vh|%)$/.test(this.config.windowHeight)) {
        console.warn('FloatingChat: windowHeight string should end with px, vw, vh or %, using default');
        this.config.windowHeight = DEFAULT_CONFIG.windowHeight;
      }
    }
  }

  private isValidColor(color: string): boolean {
    // 简单的颜色格式验证
    return /^#[0-9A-F]{6}$/i.test(color) || /^#[0-9A-F]{3}$/i.test(color);
  }

  public getConfig(): InternalConfig {
    return { ...this.config };
  }

  public updateConfig(newConfig: Partial<FloatingChatConfig>): void {
    this.config = this.mergeConfig({ ...this.config, ...newConfig });
    this.validateConfig();
  }

  public get<K extends keyof InternalConfig>(key: K): InternalConfig[K] {
    return this.config[key];
  }
}
